'use strict';

import axios from 'axios';
import axiosRetry from 'axios-retry';
import { updateSortUrls } from '@jspages/search/sortingButtons';
import { startSpinner, stopSpinner } from '@jscomponents/spinner';
import { listenDOMEvent, createElement } from '@jspartials/dom-api-utils';
import {
  gtmLaunchClickButtonShowMoreResults,
  gtmListenProductTiles
} from '@jspartials/gtm-utils';
import { customEvent } from '@jspartials/general-utils';
import { listenImagesCarousel } from '@jspages/search/imagesCarouselTiles';
import { listenVideoProductsCarousel } from '@jspages/search/videoProductsCarousel';

// Selectors
const selector = {
  wrapper: {
    gridFooter: '.js-grid-footer'
  },
  button: {
    showMore: '.js-button-show-more'
  }
};

// Variables
const eventReloadVtoButtons = new CustomEvent(customEvent.reloadVtoButtons);
const eventListenProductTiles = new CustomEvent(customEvent.listenProductTiles);
const eventInitLazyVideos = new CustomEvent(customEvent.initLazyVideos);
let infiniteScrollEnabled = false;
let observer;
let allowLoadMore = true;
const axiosClient = axios.create();

// Reintenta ante fallos de red/servidor: la restauración de páginas tras
// volver de la PDP encadena varias llamadas y una petición perdida no debe
// tirar toda la secuencia. Mismo criterio que checkout/payment/billing-address.js.
axiosRetry(axiosClient, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error) =>
    axiosRetry.isNetworkError(error) ||
    error.response?.status === 429 ||
    (error.response?.status >= 500 && error.response?.status < 600)
});

// Functions

/**
 * Handle button show more visibility
 * @param {Array} entries - Entries to observe
 * @returns {void}
 */
const handleButtonShowMoreVisibility = (entries) => {
  entries.forEach((entry) => {
    if (entry.isIntersecting && infiniteScrollEnabled) {
      loadMoreProducts(entry.target);
    }
  });
};

/**
 * Observe button show more
 * @returns {void}
 */
const observeButtonShowMore = () => {
  const buttonShowMore =
    document.querySelector(selector.button.showMore) || null;

  if (buttonShowMore) {
    // Disconnect previous observer to avoid multiples calls
    if (observer) {
      observer.disconnect();
    }

    observer = new IntersectionObserver(handleButtonShowMoreVisibility, {
      root: null,
      threshold: 0.5
    });

    observer.observe(buttonShowMore);
  }
};

/**
 * Paint new content in grid footer
 * @param {String} content - Content to paint
 * @returns {void}
 */
const paintNewContent = (content) => {
  const gridFooterElement =
    document.querySelector(selector.wrapper.gridFooter) || null;

  if (gridFooterElement) {
    const newElement = createElement({
      tag: 'div',
      content: content
    });

    gridFooterElement.replaceWith(...newElement.firstChild.childNodes);
  }
};

/**
 * Fetch a page of products and paint it into the grid
 * @param {String} url - Page url
 * @param {Object} [options] - Fetch options
 * @param {boolean} [options.trackClick] - Whether to fire the "click show more" GTM event
 * @param {boolean} [options.showSpinner] - Whether this call shows/hides its own spinner
 * @param {boolean} [options.instantReveal] - Reveal the new tiles with no entrance transition
 * @returns {Promise} - Resolves once painted, rejects if the request failed
 */
const fetchPage = (url, options) => {
  const {
    trackClick = true,
    showSpinner = true,
    instantReveal = false
  } = options || {};

  if (showSpinner) {
    startSpinner();
  }

  return axiosClient
    .get(url)
    .then((response) => {
      paintNewContent(response.data);
      updateSortUrls(response.data);

      infiniteScrollEnabled = true;
      listenImagesCarousel();
      listenVideoProductsCarousel();
      observeButtonShowMore();
      listenButtonShowMore();

      if (trackClick) {
        gtmClickButtonShowMoreResults();
      }

      gtmListenProductTiles();
      document.dispatchEvent(eventListenProductTiles);
      document.dispatchEvent(eventReloadVtoButtons);
      document.dispatchEvent(eventInitLazyVideos);
      document.dispatchEvent(
        new CustomEvent(customEvent.initProductTilesAnimation, {
          detail: { instant: instantReveal }
        })
      );
    })
    .then(
      () => {
        if (showSpinner) {
          stopSpinner();
        }
      },
      (error) => {
        if (showSpinner) {
          stopSpinner();
        }
        throw error;
      }
    );
};

/**
 * Load more products
 * @param {HTMLElement} buttonShowMore - Button show more
 * @returns {void}
 */
const loadMoreProducts = (buttonShowMore) => {
  const url = buttonShowMore?.dataset?.url || null;

  if (url && allowLoadMore) {
    allowLoadMore = false;

    fetchPage(url)
      .catch((error) => {
        console.error(error);
      })
      .then(() => {
        setTimeout(() => {
          allowLoadMore = true;
        }, 500);
      });
  }
};

/**
 * Sequentially reload the pages the user had loaded before leaving to the PDP
 *
 * Cada página siguiente depende de la URL calculada por el servidor en la
 * respuesta anterior (paginación, filtros, orden, banners intercalados...):
 * no se reconstruye a mano en cliente. Si una página agota los reintentos de
 * axios-retry, la secuencia se para ahí y se conserva lo ya cargado en vez de
 * arriesgar huecos o duplicados adivinando la siguiente URL.
 *
 * Sin spinner propio por página (lo controla quien orquesta la restauración
 * completa) y sin la animación de entrada habitual: es contenido que el
 * usuario ya vio antes de ir a la PDP, no algo nuevo que deba "aparecer".
 * @param {number} count - Number of extra pages to reload
 * @returns {Promise} - Resolves once the sequence stops (completed or cut short by a failure)
 */
const restorePreviousPages = (count) => {
  let promise = Promise.resolve();

  // Bloquea las cargas ajenas a la secuencia mientras dura. Si el navegador
  // alcanzó a aplicar su restauración de scroll antes de que la desactivemos,
  // la página queda al final y el botón "ver más" recién pintado aparece en
  // viewport: el IntersectionObserver dispararía loadMoreProducts en paralelo,
  // y dos respuestas pintando sobre el mismo .js-grid-footer descuadran la
  // paginación (la restauración no llega hasta la página del producto).
  // La secuencia no se bloquea a sí misma: llama a fetchPage directamente.
  allowLoadMore = false;

  for (let i = 0; i < count; i += 1) {
    promise = promise.then(() => {
      const buttonShowMore =
        document.querySelector(selector.button.showMore) || null;
      const url = buttonShowMore?.dataset?.url || null;

      return url
        ? fetchPage(url, {
            trackClick: false,
            showSpinner: false,
            instantReveal: true
          })
        : Promise.resolve();
    });
  }

  return promise
    .catch((error) => {
      console.error(error);
    })
    .then(() => {
      allowLoadMore = true;
    });
};

/**
 * Dispatch GTM event when user clicks on button "Show more results"
 * @returns {void}
 */
const gtmClickButtonShowMoreResults = () => {
  const gridFooterElement =
    document.querySelector(selector.wrapper.gridFooter) || null;
  const pageNumber = gridFooterElement?.dataset?.pageNumber || 0;

  gtmLaunchClickButtonShowMoreResults(pageNumber);
};

/**
 * Listen button show more event click
 * @returns {void}
 */
const listenButtonShowMore = () => {
  const buttonShowMore =
    document.querySelector(selector.button.showMore) || null;

  if (buttonShowMore) {
    listenDOMEvent(buttonShowMore, 'click', (event) => {
      event.stopPropagation();
      event.preventDefault();

      loadMoreProducts(buttonShowMore);
    });
  }
};

export { listenButtonShowMore, restorePreviousPages };
